/**
 * Minimal wrapper around LogLayer that implements our Logger interface.
 * Returns `Logger` from methods like `withContext` instead of `LogLayer`.
 */

import type { LogLayer } from 'loglayer'

import type { LogBuilder, Logger, LoggerContext, LogLevel } from './types'

export class LoggerWrapper implements Logger {
    constructor(private logger: LogLayer) {}

    withContext(context: LoggerContext): Logger {
        return new LoggerWrapper(this.logger.child().withContext(context))
    }

    withPrefix(prefix: string): Logger {
        return new LoggerWrapper(this.logger.withPrefix(prefix))
    }

    getLogger(context: LoggerContext): Logger {
        return new LoggerWrapper(this.logger.child().withContext(context))
    }

    getContext(): LoggerContext {
        return this.logger.getContext()
    }

    withMetadata(metadata: Record<string, unknown>): LogBuilder {
        return this.logger.withMetadata(metadata)
    }

    withError(error: Error): LogBuilder {
        return this.logger.withError(error)
    }

    errorOnly(error: Error, opts?: { level?: LogLevel }) {
        this.logger.errorOnly(error, { logLevel: opts?.level })
    }

    metadataOnly(metadata: Record<string, unknown>, logLevel?: LogLevel): void {
        this.logger.metadataOnly(metadata, logLevel)
    }

    info(...messages: Parameters<LogLayer['info']>) {
        return this.logger.info(...messages)
    }

    warn(...messages: Parameters<LogLayer['warn']>) {
        return this.logger.warn(...messages)
    }

    error(...messages: Parameters<LogLayer['error']>) {
        return this.logger.error(...messages)
    }

    debug(...messages: Parameters<LogLayer['debug']>) {
        return this.logger.debug(...messages)
    }

    trace(...messages: Parameters<LogLayer['trace']>) {
        return this.logger.trace(...messages)
    }

    fatal(...messages: Parameters<LogLayer['fatal']>) {
        return this.logger.fatal(...messages)
    }
}
